import React, { useEffect, useMemo, useState } from "react";
import { AUTH_ENDPOINTS } from "../utils/auth";

// ============================================================
// Icons
// ============================================================

const Icon = ({
    children,
    className = "w-5 h-5",
    onClick,
    ...props
}) => (
    <div
        className={className}
        onClick={onClick}
        {...props}
    >
        {children}
    </div>
);

const UserCheck = () => (
    <Icon className="w-4 h-4">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <polyline points="16 11 18 13 22 9" />
        </svg>
    </Icon>
);

const AlertTriangle = () => (
    <Icon className="w-4 h-4">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m21.73 18-9-15-9 15z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
        </svg>
    </Icon>
);

const ChevronRight = () => (
    <Icon className="w-4 h-4">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m9 18 6-6-6-6" />
        </svg>
    </Icon>
);

const X = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label="Dismiss notification"
        className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer hover:bg-[#F5F7FB] text-[#5B6478] transition-colors"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
        </svg>
    </button>
);

const Bell = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.375 22a2 2 0 0 0 3.25 0" />
    </svg>
);

const Clock = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const BookOpen = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
);

// ============================================================
// Brand
// ============================================================

const BRAND = "#0056D2";
const BRAND_DEEP = "#0044A8";
const MINT = "#16A34A";
// const AMBER = "#F59A3E";

// ============================================================
// Mock data
// ============================================================

const MOCK_LATEST_COURSES = [
    {
        id: 1,
        title: "React & Tailwind Masterclass",
        instructor: "Jibola James (Coach JJ)",
        progress: 65,
        imageUrl: "/assets/images/react-tailwind-course.png",
    },
    {
        id: 2,
        title: "Node.js Backend Development",
        instructor: "John Smith",
        progress: 82,
        imageUrl: "/assets/images/nodejs-course.png",
    },
    {
        id: 3,
        title: "Advanced Data Structures (Python)",
        instructor: "Prof. R.O Oladele",
        progress: 12,
        imageUrl: "/assets/images/python-course.png",
    },
    {
        id: 4,
        title: "Introduction to Computer Science",
        instructor: "Prof. R.G Jimoh",
        progress: 100,
        imageUrl: "/assets/images/intro-to-csc-course.jpg",
    },
    {
        id: 5,
        title: "Automata Theory",
        instructor: "Dr. Adebakin",
        progress: 100,
        imageUrl: "/assets/images/automata-theory-course.png",
    },
    {
        id: 6,
        title: "Operating System",
        instructor: "Dr. Hamza",
        progress: 100,
        imageUrl: "/assets/images/operating-system-course.png",
    },
];

const HOURS_SPENT = "42.5h";

// ============================================================
// Progress Ring
// ============================================================

const ProgressRing = ({
    percent,
    size = 128,
    stroke = 10,
    trackColor = "rgba(255,255,255,0.25)",
    fillColor = "#fff",
}) => {
    const safePercent = Math.min(
        100,
        Math.max(0, Number(percent) || 0)
    );

    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset =
        circumference - (safePercent / 100) * circumference;

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="-rotate-90"
            aria-hidden="true"
        >
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={trackColor}
                strokeWidth={stroke}
            />

            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={fillColor}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{
                    transition:
                        "stroke-dashoffset 700ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
            />
        </svg>
    );
};

// ============================================================
// Course Card
// ============================================================

const CourseCard = ({
    title,
    instructor,
    progress,
    imageUrl,
}) => {
    const safeProgress = Math.min(
        100,
        Math.max(0, Number(progress) || 0)
    );

    const isComplete = safeProgress >= 100;

    const barColor = isComplete
        ? "bg-[#16A34A]"
        : safeProgress < 40
            ? "bg-[#F59A3E]"
            : "bg-[#0056D2]";

    return (
        <div className="group bg-white rounded-2xl overflow-hidden border border-[#E6E9F2] flex flex-col transition-all duration-200 hover:shadow-[0_8px_30px_rgba(11,18,32,0.08)] hover:-translate-y-0.5">
            <div className="relative h-32 bg-gradient-to-br from-[#0056D2] to-[#0044A8] flex items-center justify-center overflow-hidden">
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={title}
                        className="w-full h-full object-cover absolute inset-0"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                )}

                <span className="relative text-white/90 font-semibold text-sm px-4 text-center leading-snug">
                    {title}
                </span>

                {isComplete && (
                    <span className="absolute top-2 right-2 bg-white/90 text-[#16A34A] text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full">
                        Complete
                    </span>
                )}
            </div>

            <div className="p-4 flex flex-col flex-grow">
                <h3 className="text-[15px] font-semibold text-[#0B1220] leading-snug mb-1">
                    {title}
                </h3>

                <p className="text-xs text-[#5B6478] mb-4">
                    {instructor}
                </p>

                <div className="mt-auto">
                    <div className="flex justify-between items-baseline text-xs mb-1.5">
                        <span className="text-[#5B6478] font-medium">
                            Progress
                        </span>

                        <span
                            className="font-bold tabular-nums"
                            style={{
                                color: isComplete
                                    ? MINT
                                    : BRAND,
                            }}
                        >
                            {safeProgress}%
                        </span>
                    </div>

                    <div className="w-full bg-[#E6E9F2] rounded-full h-1.5 mb-4">
                        <div
                            className={`h-1.5 rounded-full ${barColor} transition-all duration-500`}
                            style={{
                                width: `${safeProgress}%`,
                            }}
                        />
                    </div>

                    <button
                        type="button"
                        className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-semibold rounded-lg text-white transition-colors"
                        style={{
                            backgroundColor: BRAND,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                                BRAND_DEEP;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                BRAND;
                        }}
                    >
                        {isComplete
                            ? "Review Course"
                            : "Continue"}

                        <ChevronRight />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Skeleton
// ============================================================

const SkeletonLine = ({ className = "" }) => (
    <div
        className={`bg-[#E6E9F2] rounded-md animate-pulse ${className}`}
    />
);

// ============================================================
// Dashboard
// ============================================================

export default function Dashboard() {
    const [userName, setUserName] = useState("");
    const [isVerified, setIsVerified] = useState(false);
    const [showVerificationAlert, setShowVerificationAlert] =  useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const fetchUser = async () => {
            try {
                const response = await fetch(
                    AUTH_ENDPOINTS.me,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: {
                            Accept: "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch user: ${response.status} ${response.statusText}`
                    );
                }

                const data = await response.json();

                if (!isMounted) return;

                if (data?.success && data?.user) {
                    const user = data.user;

                    const fallbackName =
                        user?.email
                            ?.split("@")[0]
                            ?.trim() || "Learner";

                    setUserName(
                        user?.fullName?.trim() ||
                        fallbackName
                    );

                    setIsVerified(
                        user?.verified === true
                    );
                } else {
                    setUserName("Learner");
                    setIsVerified(false);
                }
            } catch (error) {
                console.error(
                    "Failed to fetch user data:",
                    error
                );

                if (isMounted) {
                    setUserName("Learner");
                    setIsVerified(false);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUser();

        return () => {
            isMounted = false;
        };
    }, []);

    const {
        spotlight,
        completedCount,
    } = useMemo(() => {
        const inProgress =
            MOCK_LATEST_COURSES.filter(
                (course) =>
                    Number(course.progress) < 100
            );

        const spotlight =
            [...inProgress].sort(
                (a, b) =>
                    Number(b.progress) -
                    Number(a.progress)
            )[0] ||
            MOCK_LATEST_COURSES[0];

        const completedCount =
            MOCK_LATEST_COURSES.filter(
                (course) =>
                    Number(course.progress) >= 100
            ).length;

        return {
            spotlight,
            completedCount,
        };
    }, []);

    const firstName =
        userName?.trim()?.split(/\s+/)[0] || "Learner";

    return (
        <div className="min-h-screen bg-[#F5F7FB] font-sans">
            {/* ========================================================
                Navigation
            ========================================================= */}

            <header className="sticky top-0 z-10 bg-white border-b border-[#E6E9F2]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                    <div
                        className="text-xl font-extrabold tracking-tight"
                        style={{ color: BRAND }}
                    >
                        Youdemi
                    </div>

                    <nav className="flex items-center space-x-5">
                        <a
                            href="/courses"
                            className="text-sm text-[#5B6478] hover:text-[#0B1220] transition-colors hidden sm:inline"
                        >
                            Courses
                        </a>

                        <a
                            href="/certificates"
                            className="text-sm text-[#5B6478] hover:text-[#0B1220] transition-colors hidden sm:inline"
                        >
                            Certificates
                        </a>

                        <a
                            href="/settings"
                            aria-label="Notifications"
                            className="p-2 rounded-full bg-[#F5F7FB] hover:bg-[#E6E9F2] text-[#5B6478] transition-colors"
                        >
                            <Bell />
                        </a>

                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white uppercase text-sm"
                            style={{
                                backgroundColor: BRAND,
                            }}
                            aria-label={`Account for ${firstName}`}
                        >
                            {loading
                                ? ""
                                : firstName.charAt(0) ||
                                "U"}
                        </div>
                    </nav>
                </div>
            </header>

            {/* ========================================================
                Main
            ========================================================= */}

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* ====================================================
                    Greeting
                ===================================================== */}

                <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                    <div>
                        {loading ? (
                            <>
                                <SkeletonLine className="h-9 w-64 mb-2" />
                                <SkeletonLine className="h-5 w-48" />
                            </>
                        ) : (
                            <>
                                <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0B1220] tracking-tight">
                                    Welcome back,{" "}
                                    <span
                                        style={{
                                            color: BRAND,
                                        }}
                                    >
                                        {firstName}
                                    </span>
                                </h1>

                                <p className="mt-1.5 text-[#5B6478]">
                                    Pick up where you left
                                    off.
                                </p>
                            </>
                        )}
                    </div>

                    {!loading && (
                        <span
                            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                                isVerified
                                    ? "bg-[#EAF7EE] text-[#16A34A]"
                                    : "bg-[#FFF3E8] text-[#F59A3E]"
                            }`}
                        >
                            {isVerified ? (
                                <UserCheck />
                            ) : (
                                <AlertTriangle />
                            )}

                            {isVerified
                                ? "Verified account"
                                : "Unverified"}
                        </span>
                    )}
                </div>

                {/* ====================================================
                    Verification Banner
                ===================================================== */}

                {!loading &&
                    !isVerified &&
                    showVerificationAlert && (
                        <div className="w-full p-4 mb-8 rounded-xl bg-white border border-[#E6E9F2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 text-[#F59A3E]">
                                    <AlertTriangle />
                                </div>

                                <div>
                                    <h3 className="text-sm font-semibold text-[#0B1220]">
                                        Verify your account
                                    </h3>

                                    <p className="text-sm text-[#5B6478] mt-0.5">
                                        Check your email for
                                        a verification link
                                        to unlock all
                                        features.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 self-end sm:self-auto">
                                <a
                                    href="/AccountVerificationRequest"
                                    className="py-2 px-4 text-sm font-semibold rounded-lg text-white whitespace-nowrap"
                                    style={{
                                        backgroundColor:
                                            BRAND,
                                    }}
                                >
                                    Verify now
                                </a>

                                <X
                                    onClick={() =>
                                        setShowVerificationAlert(
                                            false
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )}

                {/* ====================================================
                    Spotlight + Stats
                ===================================================== */}

                {loading ? (
                    <SkeletonLine className="h-56 w-full mb-12 rounded-2xl" />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
                        {/* Continue Learning */}

                        <div
                            className="lg:col-span-2 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-8 text-white relative overflow-hidden"
                            style={{
                                background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DEEP})`,
                            }}
                        >
                            <div className="relative flex-shrink-0">
                                <ProgressRing
                                    percent={
                                        spotlight?.progress ||
                                        0
                                    }
                                />

                                <div className="absolute inset-0 flex items-center justify-center flex-col">
                                    <span className="text-2xl font-extrabold tabular-nums">
                                        {spotlight?.progress ||
                                            0}
                                        %
                                    </span>
                                </div>
                            </div>

                            <div className="text-center sm:text-left">
                                <p className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-2">
                                    Continue learning
                                </p>

                                <h2 className="text-xl font-bold leading-snug mb-1">
                                    {spotlight?.title ||
                                        "Start learning"}
                                </h2>

                                <p className="text-sm text-white/80 mb-5">
                                    {spotlight?.instructor ||
                                        "Youdemi"}
                                </p>

                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1.5 bg-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-opacity hover:opacity-90"
                                    style={{
                                        color: BRAND,
                                    }}
                                >
                                    Resume course
                                    <ChevronRight />
                                </button>
                            </div>
                        </div>

                        {/* Stats */}

                        <div className="rounded-2xl bg-white border border-[#E6E9F2] p-6 flex flex-col justify-center divide-y divide-[#E6E9F2]">
                            <div className="flex items-center justify-between pb-4">
                                <span className="flex items-center gap-2 text-sm text-[#5B6478]">
                                    <BookOpen />
                                    Enrolled
                                </span>

                                <span className="text-xl font-bold text-[#0B1220] tabular-nums">
                                    {
                                        MOCK_LATEST_COURSES.length
                                    }
                                </span>
                            </div>

                            <div className="flex items-center justify-between py-4">
                                <span className="flex items-center gap-2 text-sm text-[#5B6478]">
                                    <Clock />
                                    Hours spent
                                </span>

                                <span className="text-xl font-bold text-[#0B1220] tabular-nums">
                                    {HOURS_SPENT}
                                </span>
                            </div>

                            <div className="flex items-center justify-between pt-4">
                                <span className="flex items-center gap-2 text-sm text-[#5B6478]">
                                    <UserCheck />
                                    Completed
                                </span>

                                <span
                                    className="text-xl font-bold tabular-nums"
                                    style={{
                                        color: MINT,
                                    }}
                                >
                                    {completedCount}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* ====================================================
                    Courses
                ===================================================== */}

                <div className="flex items-baseline justify-between mb-5">
                    <h2 className="text-xl font-bold text-[#0B1220]">
                        Your courses
                    </h2>

                    <a
                        href="/catalog"
                        className="text-sm font-semibold"
                        style={{ color: BRAND }}
                    >
                        View all
                    </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {MOCK_LATEST_COURSES.map(
                        (course) => (
                            <CourseCard
                                key={course.id}
                                {...course}
                            />
                        )
                    )}
                </div>

                {/* ====================================================
                    CTA
                ===================================================== */}

                <div className="mt-14 text-center p-10 rounded-2xl border border-[#E6E9F2] bg-white">
                    <h3 className="text-xl font-bold text-[#0B1220] mb-2">
                        Ready for the next challenge?
                    </h3>

                    <p className="text-[#5B6478] mb-6">
                        Explore thousands of courses in
                        technology, business, and the arts.
                    </p>

                    <a
                        href="/catalog"
                        className="inline-flex items-center gap-1.5 py-3 px-6 text-sm font-bold rounded-lg text-white"
                        style={{
                            backgroundColor: BRAND,
                        }}
                    >
                        Browse all courses
                        <ChevronRight />
                    </a>
                </div>
            </main>
        </div>
    );
}



// import React, { useEffect, useState } from "react";
// import { AUTH_ENDPOINTS } from "../utils/auth";
// // Assuming lucide-react or similar icons library is available
// // Using inline SVGs to ensure compatibility in the single file context
// // If using a library, replace these with actual imports like:
// // import { Bell, CheckCircle, GraduationCap, Clock, BarChart3, ChevronRight, X, UserCheck } from 'lucide-react';

// // --- Icon Definitions (Simulating lucide-react icons using inline SVG) ---
// const Icon = ({ children, className = "w-5 h-5" }) => (
//     <div className={className}>{children}</div>
// );

// const UserCheck = () => (
//     <Icon className="w-5 h-5 text-green-500"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" /></svg></Icon>
// );

// const GraduationCap = ({ className = "w-6 h-6" }) => (
//     <Icon className={className}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M12 22V10" /><path d="M12 18V10" /></svg></Icon>
// );

// const Clock = ({ className = "w-6 h-6" }) => (
//     <Icon className={className}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg></Icon>
// );

// const BarChart3 = ({ className = "w-6 h-6" }) => (
//     <Icon className={className}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></svg></Icon>
// );

// const ChevronRight = () => (
//     <Icon className="w-4 h-4"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg></Icon>
// );

// const X = ({ onClick }) => (
//     <Icon className="w-5 h-5 cursor-pointer" onClick={onClick}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg></Icon>
// );

// // --- Constants and Mock Data ---
// const PRIMARY_BLUE = 'rgb(0, 86, 210)';
// const PRIMARY_HOVER_BLUE = 'rgb(0, 68, 168)';
// const BG_LIGHT_BLUE = 'rgb(240, 248, 255)';

// const MOCK_STATS = [
//     { title: "Courses Enrolled", value: 5, icon: GraduationCap, color: 'text-indigo-500', bgColor: 'bg-indigo-100' },
//     { title: "Hours Spent", value: "42.5h", icon: Clock, color: 'text-orange-500', bgColor: 'bg-orange-100' },
//     { title: "Overall Progress", value: "75%", icon: BarChart3, color: 'text-green-500', bgColor: 'bg-green-100' },
// ];

// const MOCK_LATEST_COURSES = [
//     { id: 1, title: 'React & Tailwind Masterclass', instructor: 'Jibola James (Coach JJ)', progress: 65, imageUrl: '/assets/images/react-tailwind-course.png' },
//     { id: 2, title: 'Node.js Backend Development', instructor: 'John Smith', progress: 82, imageUrl: '/assets/images/nodejs-course.png' },
//     { id: 3, title: 'Advanced Data Structures (Python)', instructor: 'Prof. R.O Oladele', progress: 12, imageUrl: '/assets/images/python-course.png' },
//     { id: 4, title: 'Introduction to Computer Science', instructor: 'Prof. R.G Jimoh', progress: 100, imageUrl: '/assets/images/intro-to-csc-course.jpg' },
//     { id: 5, title: 'Automata Theory', instructor: 'Dr. Adebakin', progress: 100, imageUrl: '/assets/images/automata-theory-course.png' },
//     { id: 6, title: 'Operating System', instructor: 'Dr. Hamza', progress: 100, imageUrl: '/assets/images/operating-system-course.png' },
// ];

// // --- Custom Components ---

// const StatCard = ({ title, value, icon: IconComponent, color, bgColor }) => (
//     <div className="p-5 bg-white rounded-xl shadow-lg transition duration-300 hover:shadow-xl border border-gray-100">
//         <div className="flex items-center space-x-4">
//             <div className={`p-3 rounded-full ${bgColor} ${color}`}>
//                 <IconComponent className="w-6 h-6" />
//             </div>
//             <div>
//                 <p className="text-sm font-medium text-gray-500">{title}</p>
//                 <p className="text-3xl font-bold text-gray-900">{value}</p>
//             </div>
//         </div>
//     </div>
// );

// const CourseCard = ({ title, instructor, progress, imageUrl }) => {
//     const progressWidth = `${progress}%`;
//     const progressColor = progress < 50 ? 'bg-orange-500' : 'bg-green-500';

//     return (
//         <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 flex flex-col transition duration-300 hover:shadow-xl hover:scale-[1.01]">
//             {/* Image/Placeholder */}
//             <img
//                 src={imageUrl}
//                 alt={title}
//                 className="w-full h-36 object-cover"
//                 onError={(e) => {
//                     // Fallback to a styled placeholder on error
//                     e.target.onerror = null;
//                     e.target.style.display = 'none';
//                     e.target.parentNode.style.minHeight = '9rem';
//                     e.target.parentNode.style.backgroundColor = PRIMARY_BLUE;
//                     e.target.parentNode.style.color = 'white';
//                     e.target.parentNode.innerHTML = `<div class="h-full flex items-center justify-center text-xl font-bold">${title}</div>`;
//                 }}
//             />

//             <div className="p-4 flex flex-col flex-grow">
//                 <h3 className="text-xl font-bold text-gray-900 mb-1">{title}</h3>
//                 <p className="text-sm text-gray-600 mb-3">By {instructor}</p>

//                 {/* Progress Bar */}
//                 <div className="mt-auto pt-2">
//                     <div className="flex justify-between items-center text-sm mb-1">
//                         <span className="font-medium text-gray-700">Progress</span>
//                         <span className="font-semibold" style={{ color: PRIMARY_BLUE }}>{progress}%</span>
//                     </div>
//                     <div className="w-full bg-gray-200 rounded-full h-2.5">
//                         <div
//                             className={`h-2.5 rounded-full transition-all duration-500 ${progressColor}`}
//                             style={{ width: progressWidth }}
//                         ></div>
//                     </div>
//                 </div>

//                 {/* Action Button */}
//                 <button
//                     className="mt-4 w-full flex items-center justify-center py-2 text-white text-sm font-semibold rounded-lg transition duration-200"
//                     style={{ backgroundColor: PRIMARY_BLUE }}
//                     onMouseEnter={(e) => e.currentTarget.style.backgroundColor = PRIMARY_HOVER_BLUE}
//                     onMouseLeave={(e) => e.currentTarget.style.backgroundColor = PRIMARY_BLUE}
//                 >
//                     Continue Course
//                     <ChevronRight />
//                 </button>
//             </div>
//         </div>
//     );
// };


// // --- Main Dashboard Component ---
// export default function Dashboard() {
//     const [userName, setUserName] = useState('New User');
//     const [isVerified, setIsVerified] = useState(false);
//     const [showVerificationAlert, setShowVerificationAlert] = useState(true);
//     const [loading, setLoading] = useState(true);

//     // useEffect(() => {
//     //     // Retrieve and parse user data
//     //     const storedUser = localStorage.getItem('user');

//     //     if (storedUser) {
//     //         try {
//     //             const userObject = JSON.parse(storedUser);

//     //             // Use a default name if userObject.name is not available or is null/undefined
//     //             setUserName(userObject.name || userObject.email.split('@')[0] || 'Learner');
//     //             setIsVerified(userObject.verified === true);
//     //         } catch (error) {
//     //             console.error("Failed to parse user data from localStorage", error);
//     //         }
//     //     }
//     // }, []);


//     useEffect(() => {
//         fetch(AUTH_ENDPOINTS.me, { credentials: 'include' })
//             .then((res) => res.json())
//             .then((data) => {
//                 if (data.success) {
//                     const user = data.user;
//                     console.log(user);
//                     setUserName(user.fullName || user.email.split('@')[0] || 'Learner');
//                     setIsVerified(user.verified === true);
//                 }
//             })
//             .catch((error) => {
//                 console.error("Failed to fetch user data", error);
//             })
//             .finally(() => setLoading(false));
//     }, []);

//     // Verification Alert Component
//     const VerificationAlert = () => {
//         if (isVerified || !showVerificationAlert) return null;

//         return (
//             <div
//                 className="w-full p-4 mb-8 rounded-xl shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between transition-all duration-500"
//                 style={{ backgroundColor: BG_LIGHT_BLUE, borderLeft: `6px solid ${PRIMARY_BLUE}` }}
//             >
//                 <div className="flex items-start">
//                     <div className="text-2xl mr-4" style={{ color: PRIMARY_BLUE }}>
//                         {/* Exclamation Mark Icon */}
//                         <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-900">Account Verification Required</h3>
//                         <p className="text-sm text-gray-600 mt-1">
//                             Your account is unverified. Please check your email and click the verification link to unlock all features.
//                         </p>
//                     </div>
//                 </div>
//                 <div className="mt-3 md:mt-0 flex items-center space-x-4">
//                     <a
//                         href="/AccountVerificationRequest" // Placeholder for the verification link route
//                         className="py-2 px-4 text-sm font-semibold rounded-lg text-white transition duration-200 shadow-md"
//                         style={{ backgroundColor: PRIMARY_BLUE }}
//                         onMouseEnter={(e) => e.currentTarget.style.backgroundColor = PRIMARY_HOVER_BLUE}
//                         onMouseLeave={(e) => e.currentTarget.style.backgroundColor = PRIMARY_BLUE}
//                     >
//                         Verify Account Now
//                     </a>
//                     <X onClick={() => setShowVerificationAlert(false)} />
//                 </div>
//             </div>
//         );
//     };

//     return (
//         <div className="min-h-screen bg-gray-50 font-sans">

//             {/* --- Navigation Bar --- */}
//             <header className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
//                 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
//                     <div className="text-2xl font-extrabold" style={{ color: PRIMARY_BLUE }}>
//                         Youdemi
//                     </div>
//                     <nav className="flex items-center space-x-6">
//                         <a href="/courses" className="text-gray-600 hover:text-gray-900 transition duration-150">Courses</a>
//                         <a href="/certificates" className="text-gray-600 hover:text-gray-900 transition duration-150 hidden sm:inline">Certificates</a>
//                         <a href="/settings" className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition duration-150">
//                             {/* Bell Icon */}
//                             <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.375 22a2 2 0 0 0 3.25 0" /></svg>
//                         </a>
//                         <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center font-bold text-white uppercase text-lg cursor-pointer transition-all duration-300 hover:ring-2" style={{ backgroundColor: PRIMARY_BLUE }}>
//                             {userName.charAt(0)}
//                         </div>
//                     </nav>
//                 </div>
//             </header>

//             {/* --- Main Content Container --- */}
//             <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

//                 {/* --- Welcome Header and Verification Status --- */}
//                 <div className="mb-10 flex flex-col sm:flex-row justify-between items-start sm:items-end">
//                     <div>
//                         <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
//                             Hello, <span style={{ color: PRIMARY_BLUE }}>{userName}</span>
//                         </h1>
//                         <p className="mt-2 text-xl text-gray-600">
//                             Welcome back! Your path to mastery awaits.
//                         </p>
//                     </div>
//                     <div className="mt-4 sm:mt-0 flex items-center space-x-2 text-lg font-semibold">
//                         {isVerified ? (
//                             <span className="text-green-600 flex items-center">
//                                 <UserCheck />
//                                 <span className="ml-2">Verified Account</span>
//                             </span>
//                         ) : (
//                             <span className="text-orange-500 flex items-center">
//                                 {/* Alert Triangle Icon */}
//                                 <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-9-15-9 15z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
//                                 <span className="ml-2">Unverified</span>
//                             </span>
//                         )}
//                     </div>
//                 </div>

//                 {/* --- Verification Alert --- */}
//                 <VerificationAlert />

//                 {/* --- Statistics Grid --- */}
//                 <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
//                     {MOCK_STATS.map((stat, index) => (
//                         <StatCard key={index} {...stat} />
//                     ))}
//                 </div>

//                 {/* --- Continue Learning Section --- */}
//                 <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b pb-2">
//                     <span style={{ color: PRIMARY_BLUE }}>Continue</span> Learning
//                 </h2>
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//                     {MOCK_LATEST_COURSES.map((course) => (
//                         <CourseCard key={course.id} {...course} />
//                     ))}
//                 </div>

//                 {/* --- Call to Action: Explore More --- */}
//                 <div className="mt-12 text-center p-10 rounded-xl shadow-lg" style={{ backgroundColor: BG_LIGHT_BLUE }}>
//                     <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready for the Next Challenge?</h3>
//                     <p className="text-gray-700 mb-6">
//                         Explore thousands of courses in Technology, Business, and Arts.
//                     </p>
//                     <a
//                         href="/browse"
//                         className="inline-flex items-center py-3 px-6 text-lg font-bold rounded-lg text-white transition duration-200 shadow-xl"
//                         style={{ backgroundColor: PRIMARY_BLUE }}
//                         onMouseEnter={(e) => e.currentTarget.style.backgroundColor = PRIMARY_HOVER_BLUE}
//                         onMouseLeave={(e) => e.currentTarget.style.backgroundColor = PRIMARY_BLUE}
//                     >
//                         Browse All Courses
//                         <ChevronRight />
//                     </a>
//                 </div>

//             </main>
//         </div>
//     );
// }












// // import { useEffect, useState } from "react"

// // export default function Dashboard() {
// //     const [userName, setUserName] = useState('');
// //     const [isVerified, setIsVerified] = useState(false);

// //     useEffect(() => {
// //         // Retrieve the user data from localStorage
// //         const storedUser = localStorage.getItem('user');

// //         if (storedUser) {
// //             try {
// //                 const userObject = JSON.parse(storedUser);
// //                 if (userObject.name) {
// //                     setUserName(userObject.name);
// //                 }

// //                 if (userObject.verified) {
// //                     setIsVerified(true)
// //                 }
// //             } catch (error) {
// //                 console.error("Failed to parse user data from localStorage", error);
// //             }
// //         }
// //     }, []);

// //     return (
// //         <div>
// //             <h1>Dashboard</h1>
// //             {
// //                 userName ? (
// //                     <h2>Hello, <span className="name_color">{userName}</span></h2>
// //                 ) : (
// //                     <h2>Hello, User</h2>
// //                 )}
// //             <p>Welcome to your dashboard</p>
// //             <br />
// //             <br />
// //             <br />
// //             {
// //                 isVerified ? (
// //                     <h2>Hello, You  are a verified User</h2>
// //                 ) : (
// //                     <>
// //                         <h2>Hey there, you are unverified, click the button below to get verified</h2>
// //                         <a href="/verified">Verify account now</a>

// //                     </>
// //                 )
// //             }


// //             {/* <h2>Welcome, {user.email}</h2> */}
// //         </div>
// //     )
// // }